import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  People,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import AdminPageHeader from '../components/AdminPageHeader';
import customerService from '../services/customerService';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [filterLista, setFilterLista] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phone: '',
    dni: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Argentina',
    birthDate: '',
    notes: '',
    precioListaAsignada: '1'
  });

  useEffect(() => {
    loadCustomers();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    const result = await customerService.getAll({ search, precioLista: filterLista });
    if (result.success) {
      setCustomers(result.data);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const result = await customerService.getStats();
    if (result.success) {
      setStats(result.data);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        username: customer.username,
        email: customer.email,
        password: '',
        fullName: customer.fullName,
        phone: customer.phone || '',
        dni: customer.dni || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        postalCode: customer.postalCode || '',
        country: customer.country || 'Argentina',
        birthDate: customer.birthDate || '',
        notes: customer.notes || '',
        precioListaAsignada: customer.precioListaAsignada
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        fullName: '',
        phone: '',
        dni: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Argentina',
        birthDate: '',
        notes: '',
        precioListaAsignada: '1'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      fullName: '',
      phone: '',
      dni: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Argentina',
      birthDate: '',
      notes: '',
      precioListaAsignada: '1'
    });
  };

  const handleSubmit = async () => {
    // Validación
    if (!formData.username || !formData.email || !formData.fullName) {
      setMessage({ type: 'error', text: 'Completa todos los campos obligatorios' });
      return;
    }

    if (!editingCustomer && !formData.password) {
      setMessage({ type: 'error', text: 'La contraseña es obligatoria para nuevos clientes' });
      return;
    }

    if (
      formData.password
      && (
        formData.password.length < 10
        || !/[a-z]/.test(formData.password)
        || !/[A-Z]/.test(formData.password)
        || !/\d/.test(formData.password)
      )
    ) {
      setMessage({ type: 'error', text: 'La contraseña debe tener 10 caracteres, mayúscula, minúscula y número' });
      return;
    }

    let result;
    const dataToSend = { ...formData };
    
    // Si estamos editando y no se cambió la contraseña, no enviarla
    if (editingCustomer && !dataToSend.password) {
      delete dataToSend.password;
    }

    if (editingCustomer) {
      result = await customerService.update(editingCustomer.id, dataToSend);
    } else {
      result = await customerService.create(dataToSend);
    }

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      handleCloseDialog();
      loadCustomers();
      loadStats();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de desactivar este cliente?')) {
      const result = await customerService.delete(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadCustomers();
        loadStats();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  const handleActivate = async (id) => {
    if (window.confirm('¿Estás seguro de activar este cliente?')) {
      const result = await customerService.activate(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadCustomers();
        loadStats();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    loadCustomers();
  };

  const getListaLabel = (lista) => {
    return `Lista ${lista}`;
  };

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="CLIENTES"
        title="Gestión de clientes"
        subtitle="Centralizá los datos, la actividad y el historial de tus clientes."
        icon={<People />}
        action={<Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Cliente
        </Button>}
      />

      {message.text && (
        <Alert 
          severity={message.type} 
          className="mb-md"
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} className="mb-lg">
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box className="d-flex align-center justify-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Clientes
                    </Typography>
                    <Typography variant="h4">
                      {stats.total}
                    </Typography>
                  </Box>
                  <People color="primary" style={{ fontSize: 48, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box className="d-flex align-center justify-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Activos
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.activos}
                    </Typography>
                  </Box>
                  <CheckCircle color="success" style={{ fontSize: 48, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box className="d-flex align-center justify-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Inactivos
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {stats.inactivos}
                    </Typography>
                  </Box>
                  <Cancel color="error" style={{ fontSize: 48, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Por Lista de Precios
                </Typography>
                {stats.porLista.map(item => (
                  <Box key={item.lista} className="d-flex justify-between mb-sm">
                    <Typography variant="body2">Lista {item.lista}:</Typography>
                    <Typography variant="body2" className="font-bold">{item.count}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Paper className="p-md mb-md">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, usuario o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Lista</InputLabel>
              <Select
                value={filterLista}
                label="Filtrar por Lista"
                onChange={(e) => setFilterLista(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="1">Lista 1</MenuItem>
                <MenuItem value="2">Lista 2</MenuItem>
                <MenuItem value="3">Lista 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de clientes */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Nombre Completo</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell>Lista de Precios</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay clientes registrados
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>{customer.username}</TableCell>
                  <TableCell>{customer.fullName}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone || '-'}</TableCell>
                  <TableCell>{customer.city || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getListaLabel(customer.precioListaAsignada)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {customer.isActive ? (
                      <Chip label="Activo" color="success" size="small" />
                    ) : (
                      <Chip label="Inactivo" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(customer)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                    {customer.isActive ? (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(customer.id)}
                        title="Desactivar"
                      >
                        <Delete />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleActivate(customer.id)}
                        title="Activar"
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar cliente */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
        </DialogTitle>
        <DialogContent>
          <Box className="mt-sm">
            {/* Datos Básicos */}
            <Typography variant="h6" className="mb-md" color="primary">
              Datos Básicos
            </Typography>
            <Grid container spacing={2} className="mb-lg">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario *"
                  value={formData.username}
                  onChange={(e) => handleFormChange('username', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre Completo *"
                  value={formData.fullName}
                  onChange={(e) => handleFormChange('fullName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={editingCustomer ? 'Nueva Contraseña (opcional)' : 'Contraseña *'}
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  helperText="Mínimo 6 caracteres"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Lista de Precios *</InputLabel>
                  <Select
                    value={formData.precioListaAsignada}
                    label="Lista de Precios *"
                    onChange={(e) => handleFormChange('precioListaAsignada', e.target.value)}
                  >
                    <MenuItem value="1">Lista 1</MenuItem>
                    <MenuItem value="2">Lista 2</MenuItem>
                    <MenuItem value="3">Lista 3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Datos de Contacto */}
            <Typography variant="h6" className="mb-md" color="primary">
              Contacto e Identificación
            </Typography>
            <Grid container spacing={2} className="mb-lg">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  placeholder="+54 9 11 1234-5678"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DNI / CUIT"
                  value={formData.dni}
                  onChange={(e) => handleFormChange('dni', e.target.value)}
                  placeholder="12345678"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha de Nacimiento"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleFormChange('birthDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Dirección */}
            <Typography variant="h6" className="mb-md" color="primary">
              Dirección
            </Typography>
            <Grid container spacing={2} className="mb-lg">
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dirección"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  placeholder="Av. Corrientes 1234"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ciudad"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  placeholder="Buenos Aires"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Provincia / Estado"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  placeholder="CABA"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Código Postal"
                  value={formData.postalCode}
                  onChange={(e) => handleFormChange('postalCode', e.target.value)}
                  placeholder="C1043"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="País"
                  value={formData.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Notas */}
            <Typography variant="h6" className="mb-md" color="primary">
              Notas Adicionales
            </Typography>
            <TextField
              fullWidth
              label="Notas"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Información adicional sobre el cliente..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCustomer ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;

